'use client'

import { useQuery } from '@tanstack/react-query'
import { api, queryKeys } from '@/lib/api'

type AttachmentUrlQueryData = {
  url: string
  expiresAt: number | null
}

type UseAttachmentUrlOptions = {
  s3Key?: string | null
  initialUrl?: string | null
}

export const useAttachmentUrl = ({
  s3Key,
  initialUrl,
}: UseAttachmentUrlOptions) => {
  const normalizedKey =
    typeof s3Key === 'string' && s3Key.trim().length > 0 ? s3Key.trim() : null

  const normalizedInitialUrl =
    typeof initialUrl === 'string' && initialUrl.trim().length > 0
      ? initialUrl.trim()
      : null

  const query = useQuery<AttachmentUrlQueryData>({
    queryKey: queryKeys.attachments.presignedUrl(
      normalizedKey ?? 'unresolved-attachment'
    ),
    queryFn: async () => {
      if (!normalizedKey) {
        throw new Error('Attachment key is required to fetch presigned URL')
      }

      const response = await api.upload.getPresignedUrl(normalizedKey)

      if (!response.success || !response.data?.presignedUrl) {
        throw new Error(
          response.error || 'Failed to resolve attachment download URL'
        )
      }

      const expiresInSeconds =
        typeof response.data.expiresIn === 'number' &&
        Number.isFinite(response.data.expiresIn)
          ? response.data.expiresIn
          : null

      return {
        url: response.data.presignedUrl,
        expiresAt:
          expiresInSeconds !== null
            ? Date.now() + expiresInSeconds * 1000
            : null,
      }
    },
    enabled: Boolean(normalizedKey),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
    placeholderData: normalizedInitialUrl
      ? {
          url: normalizedInitialUrl,
          expiresAt: null,
        }
      : undefined,
  })

  return {
    url: query.data?.url ?? normalizedInitialUrl ?? null,
    expiresAt: query.data?.expiresAt ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
